if (!customElements.get('product-discount-price')) {
  // Separate Storefront carts quote Shopify discounts without touching /cart.js,
  // the customer's cart cookie, or checkout. Cache briefly to limit requests.
  const quotes = new Map();
  const query = `mutation ProductDiscountQuote($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        discountAllocations { discountedAmount { amount currencyCode } }
        lines(first: 100) {
          nodes {
            quantity
            cost {
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
          }
        }
      }
      userErrors { message }
    }
  }`;

  customElements.define('product-discount-price', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.getAttribute('form'));
      this.revision = 0;
      if (!this.form || this.dataset.available !== 'true') return;
      this.onChange = (event) => {
        if (event.target.form === this.form && ['quantity', 'selling_plan', 'id'].includes(event.target.name)) this.schedule();
      };
      this.onBundleChange = () => this.schedule();
      document.addEventListener('input', this.onChange);
      document.addEventListener('change', this.onChange);
      this.form.addEventListener('bundle:change', this.onBundleChange);
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.revision++;
      document.removeEventListener('input', this.onChange);
      document.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('bundle:change', this.onBundleChange);
    }

    schedule() {
      this.hidden = true;
      this.revision++;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(this.revision), 250);
    }

    async update(revision) {
      const quantity = Number(this.form.elements.namedItem('quantity')?.value ?? 1);
      if (!Number.isInteger(quantity) || quantity < 1) return;
      const line = { merchandiseId: `gid://shopify/ProductVariant/${this.dataset.variantId}`, quantity };
      const sellingPlan = this.form.elements.namedItem('selling_plan')?.value;
      if (sellingPlan) line.sellingPlanId = `gid://shopify/SellingPlan/${sellingPlan}`;
      const bundles = Array.from(this.form.bundles ?? []);
      const lines = [line, ...bundles.map((input) => ({
        merchandiseId: `gid://shopify/ProductVariant/${input.value}`, quantity: 1
      }))];
      const input = { lines, buyerIdentity: { countryCode: this.dataset.country } };
      const key = JSON.stringify(input);
      try {
        let cached = quotes.get(key);
        if (!cached || Date.now() - cached.created > 30000) {
          cached = {
            created: Date.now(),
            promise: fetch('/api/2026-07/graphql.json', {
              method: 'POST',
              credentials: 'omit',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ query, variables: { input } }),
              signal: AbortSignal.timeout(10000)
            }).then(async (response) => {
              if (!response.ok) throw new Error('Discount quote unavailable');
              const result = await response.json();
              const payload = result.data?.cartCreate;
              if (result.errors?.length || payload?.userErrors?.length || !payload?.cart) throw new Error('Discount quote unavailable');
              return payload.cart;
            })
          };
          if (quotes.size >= 50) quotes.delete(quotes.keys().next().value);
          quotes.set(key, cached);
        }
        const cart = await cached.promise;
        if (!this.isConnected || revision !== this.revision) return;
        const quotedLines = cart.lines.nodes;
        // Fail closed if Shopify could not quote the complete purchase selection.
        if (quotedLines.reduce((total, item) => total + item.quantity, 0) !== lines.reduce((total, item) => total + item.quantity, 0)) return;
        const currency = quotedLines[0]?.cost.totalAmount.currencyCode;
        if (!currency || currency !== this.dataset.currency) return;
        const original = quotedLines.reduce((total, item) => total + Number(item.cost.subtotalAmount.amount), 0);
        const discounted = quotedLines.reduce((total, item) => total + Number(item.cost.totalAmount.amount), 0)
          - cart.discountAllocations.reduce((total, allocation) => total + Number(allocation.discountedAmount.amount), 0);
        if (!Number.isFinite(original) || !Number.isFinite(discounted) || discounted < 0 || Math.round(discounted * 100) >= Math.round(original * 100)) return;
        const format = (amount) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency, currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: Number.isInteger(amount) ? 0 : undefined
        }).format(amount);
        this.querySelector('[data-final]').textContent = format(discounted);
        this.hidden = false;
      } catch (_) {
        quotes.delete(key);
        // Keep the normal product price and purchase buttons usable on failures.
        if (revision === this.revision) this.hidden = true;
      }
    }
  });
}
